(() => {
  'use strict';

  const VERSION = 'aa_attribution_2026_06_18_v1';
  const STORAGE_KEY = 'aa_attribution_v1';
  const SYNC_KEY = 'aa_attribution_cart_sync_v1';
  const DEBUG_PARAM = 'aa_debug_attribution';
  const MAX_URL = 220;
  const MAX_VALUE = 120;
  const PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'fbclid',
    'msclkid',
    'ttclid',
    'twclid',
    'li_fat_id',
  ];
  const CLICK_IDS = ['gclid', 'fbclid', 'msclkid', 'ttclid', 'twclid', 'li_fat_id'];

  const debug = new URLSearchParams(window.location.search).get(DEBUG_PARAM) === '1';

  function log(...args) {
    if (debug) console.info('[aa-attribution]', ...args);
  }

  function truncate(value, max = MAX_VALUE) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, max);
  }

  function safeUrl(value, includeAllowedQuery = false) {
    try {
      const url = new URL(value, window.location.origin);
      const safe = `${url.origin}${url.pathname}`.replace(/\/$/, '/') || '/';
      if (!includeAllowedQuery) return truncate(safe, MAX_URL);
      const allowed = new URLSearchParams();
      PARAMS.forEach((param) => {
        const paramValue = url.searchParams.get(param);
        if (paramValue) allowed.set(param, truncate(paramValue, 80));
      });
      const query = allowed.toString();
      return truncate(query ? `${safe}?${query}` : safe, MAX_URL);
    } catch {
      return '';
    }
  }

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return readCookie(key);
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Cookie fallback below.
    }
    writeCookie(key, value);
  }

  function readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeCookie(name, value) {
    try {
      document.cookie = `${name}=${encodeURIComponent(value).slice(0, 3800)}; path=/; max-age=7776000; SameSite=Lax`;
    } catch {
      // Ignore storage failures.
    }
  }

  function readRecord() {
    try {
      const raw = storageGet(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  function writeRecord(record) {
    storageSet(STORAGE_KEY, JSON.stringify(record));
  }

  function paramsObject() {
    const params = new URLSearchParams(window.location.search);
    return PARAMS.reduce((out, param) => {
      const value = params.get(param);
      if (value) out[param] = truncate(value, param.startsWith('utm_') ? 120 : 80);
      return out;
    }, {});
  }

  function classifyReferrer(referrer) {
    if (!referrer) return null;

    try {
      const url = new URL(referrer);
      if (url.hostname === window.location.hostname) return null;
      const host = url.hostname.replace(/^www\./, '').toLowerCase();
      const rules = [
        [/google\./, ['google', 'organic']],
        [/bing\./, ['bing', 'organic']],
        [/yahoo\./, ['yahoo', 'organic']],
        [/duckduckgo\./, ['duckduckgo', 'organic']],
        [/(instagram\.com|l\.instagram\.com)/, ['instagram', 'social']],
        [/(facebook\.com|l\.facebook\.com|m\.facebook\.com)/, ['facebook', 'social']],
        [/(tiktok\.com|t\.co\/.*tiktok)/, ['tiktok', 'social']],
        [/pinterest\./, ['pinterest', 'social']],
        [/youtube\./, ['youtube', 'social']],
        [/reddit\./, ['reddit', 'social']],
        [/linkedin\./, ['linkedin', 'social']],
        [/(twitter\.com|x\.com|t\.co)/, ['x-twitter', 'social']],
      ];
      const found = rules.find(([pattern]) => pattern.test(host));
      return found ? { source: found[1][0], medium: found[1][1] } : { source: host, medium: 'referral' };
    } catch {
      return null;
    }
  }

  function clickSource(params) {
    if (params.gclid) return { source: 'google', medium: 'cpc' };
    if (params.msclkid) return { source: 'bing', medium: 'cpc' };
    if (params.fbclid) return { source: 'facebook', medium: 'social' };
    if (params.ttclid) return { source: 'tiktok', medium: 'social' };
    if (params.twclid) return { source: 'x-twitter', medium: 'social' };
    if (params.li_fat_id) return { source: 'linkedin', medium: 'social' };
    return null;
  }

  function buildTouch() {
    const params = paramsObject();
    const ref = classifyReferrer(document.referrer);
    const click = clickSource(params);
    const source = params.utm_source || click?.source || ref?.source || 'direct';
    const medium = params.utm_medium || click?.medium || ref?.medium || 'none';
    const campaign = params.utm_campaign || (click ? 'click_id' : '');
    const clickIds = CLICK_IDS.filter((id) => params[id]);

    return {
      source: truncate(source, 60).toLowerCase(),
      medium: truncate(medium, 60).toLowerCase(),
      campaign: truncate(campaign, 100).toLowerCase(),
      landing_page: safeUrl(window.location.href, true),
      referrer: document.referrer ? safeUrl(document.referrer, false) : '',
      click_ids: clickIds,
      hasSignal: Boolean(params.utm_source || params.utm_medium || params.utm_campaign || clickIds.length || ref),
    };
  }

  function touchChanged(left, right) {
    if (!left) return true;
    return ['source', 'medium', 'campaign', 'landing_page', 'referrer'].some((key) => left[key] !== right[key]);
  }

  function captureAttribution() {
    const record = readRecord();
    const now = new Date().toISOString();
    const touch = buildTouch();

    record.v = VERSION;
    record.session_ts = now;

    if (!record.first) {
      record.first = {
        source: touch.source,
        medium: touch.medium,
        campaign: touch.campaign,
        landing_page: touch.landing_page,
        referrer: touch.referrer,
        ts: now,
      };
    }

    if (!record.last || (touch.hasSignal && touchChanged(record.last, touch))) {
      record.last = {
        source: touch.source,
        medium: touch.medium,
        campaign: touch.campaign,
        landing_page: touch.landing_page,
        referrer: touch.referrer,
        ts: now,
      };
    }

    const existingClickIds = Array.isArray(record.click_ids) ? record.click_ids : [];
    record.click_ids = [...new Set([...existingClickIds, ...touch.click_ids])].slice(0, CLICK_IDS.length);

    writeRecord(record);
    pushDataLayer('aa_attribution_captured', {
      source: record.last?.source,
      medium: record.last?.medium,
      click_ids: record.click_ids,
    });
    log('captured', record);
    return record;
  }

  function pushDataLayer(event, payload) {
    if (!Array.isArray(window.dataLayer)) return;
    window.dataLayer.push({
      event,
      aa_attribution_version: VERSION,
      ...(payload || {}),
    });
  }

  function cartRoot() {
    const root = window.Shopify?.routes?.root || window.theme?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function cartAttributes(record) {
    const first = record.first || {};
    const last = record.last || {};
    return {
      aa_first_source: truncate(first.source, 60),
      aa_first_medium: truncate(first.medium, 60),
      aa_first_campaign: truncate(first.campaign, 100),
      aa_first_landing_page: truncate(first.landing_page, MAX_URL),
      aa_first_referrer: truncate(first.referrer, MAX_URL),
      aa_last_source: truncate(last.source, 60),
      aa_last_medium: truncate(last.medium, 60),
      aa_last_campaign: truncate(last.campaign, 100),
      aa_last_landing_page: truncate(last.landing_page, MAX_URL),
      aa_last_referrer: truncate(last.referrer, MAX_URL),
      aa_click_ids: truncate((record.click_ids || []).join(','), 120),
      aa_attribution_version: VERSION,
    };
  }

  async function fetchCart() {
    const response = await fetch(`${cartRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  async function syncCartAttribution(options = {}) {
    const record = readRecord();
    if (!record.first || !record.last) return;

    try {
      const cart = await fetchCart();
      if (!cart || !cart.item_count) {
        log('cart empty; sync deferred');
        return;
      }

      const attributes = cartAttributes(record);
      const signature = JSON.stringify(attributes);
      const cartToken = cart.token || 'cart';
      const syncSignature = `${cartToken}:${signature}`;
      if (!options.force && storageGet(SYNC_KEY) === syncSignature) {
        log('cart attributes already synced');
        return;
      }

      const response = await fetch(`${cartRoot()}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error(`cart/update.js ${response.status}`);
      storageSet(SYNC_KEY, syncSignature);
      pushDataLayer('aa_cart_attribution_synced', {
        source: record.last.source,
        medium: record.last.medium,
      });
      log('cart attributes synced', attributes);
    } catch (error) {
      log('cart sync skipped', error && error.message ? error.message : error);
    }
  }

  function scheduleSync(delay = 500, options = {}) {
    window.setTimeout(() => syncCartAttribution(options), delay);
  }

  function bindEvents() {
    document.addEventListener('theme:cart:add', () => {
      scheduleSync(700, { force: true });
      scheduleSync(1600, { force: true });
    });
    document.addEventListener('theme:cart:change', () => scheduleSync(500, { force: true }));
    document.addEventListener('theme:cart-drawer:open', () => scheduleSync(250));
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form instanceof HTMLFormElement && /\/cart(\/)?$/i.test(form.action || '')) {
        scheduleSync(0, { force: true });
      }
    }, true);
    document.addEventListener('pointerdown', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[name="checkout"], .cart__checkout, [data-cart-checkout-button]')) {
        scheduleSync(0, { force: true });
      }
    }, true);
  }

  captureAttribution();
  bindEvents();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleSync(400));
  } else {
    scheduleSync(400);
  }

  window.AAAttribution = {
    version: VERSION,
    capture: captureAttribution,
    syncCart: () => syncCartAttribution({ force: true }),
  };
})();
